import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient, type PaginatedResult } from './api-client';

/**
 * The sales pipeline: inquiries, quotation requests and quotations
 * (spec §17, §18, §34, §35).
 *
 * Every write goes through a state machine on the server, which decides both
 * whether a move is legal and what it costs. Nothing here reasons about that —
 * it posts the intent and re-reads what the server returns.
 */

// --- Inquiries ---------------------------------------------------------------

export type InquiryStatus =
  | 'NEW'
  | 'CONTACTED'
  | 'QUALIFIED'
  | 'QUOTATION_SENT'
  | 'NEGOTIATION'
  | 'WON'
  | 'LOST'
  | 'CLOSED';

export type InquiryPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

export type InquirySource =
  | 'WEBSITE_FORM'
  | 'AI_CHAT'
  | 'PHONE'
  | 'EMAIL'
  | 'WHATSAPP'
  | 'REFERRAL'
  | 'TRADE_SHOW'
  | 'OTHER';

export type InquiryActivityType =
  | 'NOTE'
  | 'CALL'
  | 'EMAIL'
  | 'MEETING'
  | 'STATUS_CHANGE'
  | 'ASSIGNMENT'
  | 'QUOTATION_SENT';

export interface InquiryRow {
  id: string;
  inquiryNumber: string;
  name: string;
  companyName: string | null;
  email: string;
  phone: string;
  productInterest: string | null;
  quantity: number | null;
  budget: number | null;
  source: InquirySource;
  status: InquiryStatus;
  priority: InquiryPriority;
  createdAt: string;
  customer: { id: string; customerCode: string; companyName: string } | null;
  assignedTo: { id: string; name: string } | null;
}

export interface InquiryActivity {
  id: string;
  type: InquiryActivityType;
  note: string | null;
  performedBy: { id: string; name: string } | null;
  createdAt: string;
}

export interface InquiryDetail extends InquiryRow {
  productId: string | null;
  product: { id: string; name: string; slug: string } | null;
  customizationRequirement: string | null;
  deliveryLocation: string | null;
  expectedDeliveryDate: string | null;
  message: string | null;
  assignedAt: string | null;
  lostReason: string | null;
  wonAt: string | null;
  closedAt: string | null;
  activities: InquiryActivity[];
  updatedAt: string;
}

export interface InquiryListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: InquiryStatus[];
  priority?: InquiryPriority;
  assignedToId?: string;
  unassignedOnly?: boolean;
}

const inquiryKeys = {
  all: ['admin', 'inquiries'] as const,
  list: (params: InquiryListParams) => [...inquiryKeys.all, 'list', params] as const,
  summary: () => [...inquiryKeys.all, 'summary'] as const,
  detail: (id: string) => [...inquiryKeys.all, 'detail', id] as const,
};

export function useInquiries(params: InquiryListParams) {
  return useQuery({
    queryKey: inquiryKeys.list(params),
    queryFn: () =>
      apiClient.get<PaginatedResult<InquiryRow>>('/admin/inquiries', {
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search || undefined,
          status: params.status?.length ? params.status.join(',') : undefined,
          priority: params.priority,
          assignedToId: params.assignedToId,
          // Sent only when on — `unassignedOnly=false` means "no filter".
          unassignedOnly: params.unassignedOnly || undefined,
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export function useInquirySummary() {
  return useQuery({
    queryKey: inquiryKeys.summary(),
    queryFn: () => apiClient.get<Record<string, number>>('/admin/inquiries/summary'),
  });
}

export function useInquiry(inquiryId: string | null) {
  return useQuery({
    queryKey: inquiryKeys.detail(inquiryId ?? ''),
    queryFn: () => apiClient.get<InquiryDetail>(`/admin/inquiries/${inquiryId}`),
    enabled: Boolean(inquiryId),
  });
}

function useInquiryMutation<TVariables>(mutationFn: (variables: TVariables) => Promise<unknown>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: inquiryKeys.all });
    },
  });
}

export function useChangeInquiryStatus() {
  return useInquiryMutation(
    ({
      inquiryId,
      ...body
    }: {
      inquiryId: string;
      status: InquiryStatus;
      reason?: string;
      notes?: string;
    }) => apiClient.patch(`/admin/inquiries/${inquiryId}/status`, body),
  );
}

export function useAssignInquiry() {
  return useInquiryMutation(
    ({
      inquiryId,
      ...body
    }: {
      inquiryId: string;
      assignedToId?: string;
      priority?: InquiryPriority;
    }) => apiClient.patch(`/admin/inquiries/${inquiryId}/assignment`, body),
  );
}

export function useLogInquiryActivity() {
  return useInquiryMutation(
    ({
      inquiryId,
      ...body
    }: {
      inquiryId: string;
      type: InquiryActivityType;
      note: string;
    }) => apiClient.post(`/admin/inquiries/${inquiryId}/activities`, body),
  );
}

// --- Quotations --------------------------------------------------------------

export type QuotationStatus =
  | 'DRAFT'
  | 'SENT'
  | 'VIEWED'
  | 'NEGOTIATION'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'EXPIRED'
  | 'CONVERTED_TO_ORDER';

export interface QuotationItem {
  id: string;
  productId: string | null;
  productName: string;
  productSku: string | null;
  description: string | null;
  hsnCode: string | null;
  quantity: number;
  unitPrice: number;
  discountAmount: number;
  taxPercent: number;
  taxAmount: number;
  lineTotal: number;
  notes: string | null;
}

export interface QuotationRow {
  id: string;
  quotationNumber: string;
  status: QuotationStatus;
  totalAmount: number;
  currency: string;
  itemCount: number;
  validUntil: string;
  isExpired: boolean;
  version: number;
  sentAt: string | null;
  createdAt: string;
  customer: { id: string; customerCode: string; companyName: string } | null;
  createdBy: { id: string; name: string } | null;
}

export interface QuotationDetail extends QuotationRow {
  subtotal: number;
  discountAmount: number;
  taxAmount: number;
  shippingAmount: number;
  additionalCharges: number;
  paymentTerms: string | null;
  deliveryTerms: string | null;
  terms: string | null;
  notes: string | null;
  /** Staff-only. Never returned to the buyer. */
  internalNotes: string | null;
  viewedAt: string | null;
  respondedAt: string | null;
  rejectionReason: string | null;
  revisionOfId: string | null;
  items: QuotationItem[];
  statusHistory: Array<{
    id: string;
    fromStatus: QuotationStatus | null;
    toStatus: QuotationStatus;
    notes: string | null;
    changedBy: string | null;
    createdAt: string;
  }>;
  convertedOrder: { id: string; orderNumber: string } | null;
  quotationRequest: { id: string; requestNumber: string } | null;
}

export interface QuotationLineInput {
  productId?: string;
  productName: string;
  productSku?: string;
  description?: string;
  hsnCode?: string;
  quantity: number;
  unitPrice: number;
  discountAmount?: number;
  taxPercent?: number;
  notes?: string;
}

export interface QuotationInput {
  customerId: string;
  quotationRequestId?: string;
  inquiryId?: string;
  items: QuotationLineInput[];
  discountAmount?: number;
  shippingAmount?: number;
  additionalCharges?: number;
  validUntil?: string;
  paymentTerms?: string;
  deliveryTerms?: string;
  terms?: string;
  notes?: string;
  internalNotes?: string;
}

export interface QuotationListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: QuotationStatus[];
  customerId?: string;
  awaitingResponseOnly?: boolean;
}

const quotationKeys = {
  all: ['admin', 'quotations'] as const,
  list: (params: QuotationListParams) => [...quotationKeys.all, 'list', params] as const,
  summary: () => [...quotationKeys.all, 'summary'] as const,
  detail: (id: string) => [...quotationKeys.all, 'detail', id] as const,
};

export function useQuotations(params: QuotationListParams) {
  return useQuery({
    queryKey: quotationKeys.list(params),
    queryFn: () =>
      apiClient.get<PaginatedResult<QuotationRow>>('/admin/quotations', {
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search || undefined,
          status: params.status?.length ? params.status.join(',') : undefined,
          customerId: params.customerId,
          awaitingResponseOnly: params.awaitingResponseOnly || undefined,
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export function useQuotationSummary() {
  return useQuery({
    queryKey: quotationKeys.summary(),
    queryFn: () => apiClient.get<Record<string, number>>('/admin/quotations/summary'),
  });
}

export function useQuotation(quotationId: string | null) {
  return useQuery({
    queryKey: quotationKeys.detail(quotationId ?? ''),
    queryFn: () => apiClient.get<QuotationDetail>(`/admin/quotations/${quotationId}`),
    enabled: Boolean(quotationId),
  });
}

/**
 * A quotation write invalidates the quotation queries and the inquiries.
 *
 * Raising a quotation against a lead moves that lead to QUOTATION_SENT in the
 * same transaction, so the pipeline board is stale the moment this succeeds.
 */
function useQuotationMutation<TVariables>(mutationFn: (variables: TVariables) => Promise<unknown>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: quotationKeys.all });
      void queryClient.invalidateQueries({ queryKey: inquiryKeys.all });
    },
  });
}

export function useCreateQuotation() {
  return useQuotationMutation((body: QuotationInput) => apiClient.post('/admin/quotations', body));
}

export function useUpdateQuotation() {
  return useQuotationMutation(({ quotationId, ...body }: QuotationInput & { quotationId: string }) =>
    apiClient.patch(`/admin/quotations/${quotationId}`, body),
  );
}

export function useSendQuotation() {
  return useQuotationMutation(({ quotationId, notes }: { quotationId: string; notes?: string }) =>
    apiClient.post(`/admin/quotations/${quotationId}/send`, { notes }),
  );
}

export function useReviseQuotation() {
  return useQuotationMutation(({ quotationId, ...body }: QuotationInput & { quotationId: string }) =>
    apiClient.post(`/admin/quotations/${quotationId}/revisions`, body),
  );
}

// --- Quotation requests ------------------------------------------------------

export type RfqStatus = 'NEW' | 'ASSIGNED' | 'QUOTED' | 'CLOSED' | 'CANCELLED';

export interface RfqRow {
  id: string;
  requestNumber: string;
  productId: string | null;
  product: { id: string; name: string; slug: string } | null;
  productName: string | null;
  quantity: number;
  targetPrice: number | null;
  deliveryLocation: string | null;
  requiredByDate: string | null;
  customizationRequired: boolean;
  brandingRequirements: string | null;
  packagingRequirements: string | null;
  notes: string | null;
  status: RfqStatus;
  quotationCount: number;
  createdAt: string;
  customer: { id: string; customerCode: string; companyName: string } | null;
  assignedTo: { id: string; name: string } | null;
}

export function useQuotationRequests(params: { page?: number; status?: RfqStatus[]; search?: string }) {
  return useQuery({
    queryKey: ['admin', 'quotation-requests', params] as const,
    queryFn: () =>
      apiClient.get<PaginatedResult<RfqRow>>('/admin/quotation-requests', {
        query: {
          page: params.page,
          status: params.status?.length ? params.status.join(',') : undefined,
          search: params.search || undefined,
        },
      }),
    placeholderData: (previous) => previous,
  });
}

// --- Presentation helpers ----------------------------------------------------

export const INQUIRY_STATUS_LABEL: Record<InquiryStatus, string> = {
  NEW: 'New',
  CONTACTED: 'Contacted',
  QUALIFIED: 'Qualified',
  QUOTATION_SENT: 'Quotation sent',
  NEGOTIATION: 'Negotiation',
  WON: 'Won',
  LOST: 'Lost',
  CLOSED: 'Closed',
};

export const INQUIRY_PRIORITY_LABEL: Record<InquiryPriority, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  URGENT: 'Urgent',
};

export const INQUIRY_ACTIVITY_LABEL: Record<InquiryActivityType, string> = {
  NOTE: 'Note',
  CALL: 'Call',
  EMAIL: 'Email',
  MEETING: 'Meeting',
  STATUS_CHANGE: 'Status change',
  ASSIGNMENT: 'Assignment',
  QUOTATION_SENT: 'Quotation sent',
};

export const QUOTATION_STATUS_LABEL: Record<QuotationStatus, string> = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  VIEWED: 'Viewed',
  NEGOTIATION: 'Negotiation',
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
  CONVERTED_TO_ORDER: 'Ordered',
};

export const RFQ_STATUS_LABEL: Record<RfqStatus, string> = {
  NEW: 'New',
  ASSIGNED: 'Assigned',
  QUOTED: 'Quoted',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

/**
 * Copies of the server's transition maps, so a control offers only legal moves
 * instead of a dropdown of everything followed by a rejection.
 *
 * The server re-checks every transition, so a stale copy costs a failed write,
 * never an illegal one. Same trade-off — and same debt — as `ORDER_TRANSITIONS`
 * in `orders-api.ts`.
 */
export const INQUIRY_TRANSITIONS: Record<InquiryStatus, readonly InquiryStatus[]> = {
  NEW: ['CONTACTED', 'QUALIFIED', 'LOST', 'CLOSED'],
  CONTACTED: ['QUALIFIED', 'QUOTATION_SENT', 'LOST', 'CLOSED'],
  QUALIFIED: ['QUOTATION_SENT', 'NEGOTIATION', 'LOST', 'CLOSED'],
  QUOTATION_SENT: ['NEGOTIATION', 'WON', 'LOST', 'CLOSED'],
  NEGOTIATION: ['QUOTATION_SENT', 'WON', 'LOST', 'CLOSED'],
  WON: ['CLOSED'],
  LOST: ['CONTACTED', 'CLOSED'],
  CLOSED: [],
};
